import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useSwitchChain, useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { parseUnits, formatUnits, type Address } from 'viem';
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { Button } from "@/components/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/card";
import { Input } from "@/components/input";
import { Label } from "@/components/label";
import {
  RiAlertLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiLoader4Line,
  RiCoinsLine,
  RiTimeLine,
  RiArrowDownLine,
  RiHistoryLine,
} from "@remixicon/react";
import { ERC20_ABI } from "@/abis/erc20";
import { SILO_MIGRATION_ABI, SILO_MIGRATION_CONTRACT } from "@/abis/silo-migration";
import { larissaChain } from "@/configs/chain";

// Vesting Contract ABI (simplified for getVestingInfo)
const VESTING_ABI = [
  {
    "inputs": [{"internalType": "address", "name": "user", "type": "address"}],
    "name": "getVestingInfo",
    "outputs": [
      {
        "components": [
          {"internalType": "uint256", "name": "totalAmount", "type": "uint256"},
          {"internalType": "uint256", "name": "claimedAmount", "type": "uint256"},
          {"internalType": "uint256", "name": "claimableAmount", "type": "uint256"},
          {"internalType": "uint256", "name": "startTime", "type": "uint256"},
          {"internalType": "uint256", "name": "nextClaimTime", "type": "uint256"}
        ],
        "internalType": "struct IVestingContract.VestingInfo",
        "name": "",
        "type": "tuple"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  }
] as const;

export default function MigrationSiloPage() {
  const { address, isConnected, chain } = useAccount();
  const { switchChain } = useSwitchChain();

  // State management
  const [amount, setAmount] = useState("");
  const [isApproving, setIsApproving] = useState(false);
  const [isDepositing, setIsDepositing] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimingDepositId, setClaimingDepositId] = useState<bigint | null>(null);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  // Check if on Larissa chain
  const isOnLarissaChain = chain?.id === larissaChain.id;

  // Get migration token address from contract
  const { data: migrationTokenAddress } = useReadContract({
    address: SILO_MIGRATION_CONTRACT,
    abi: SILO_MIGRATION_ABI,
    functionName: 'migrationToken',
    chainId: larissaChain.id,
    query: {
      enabled: isConnected && isOnLarissaChain,
    }
  });

  // Get vesting contract address
  const { data: vestingContractAddress } = useReadContract({
    address: SILO_MIGRATION_CONTRACT,
    abi: SILO_MIGRATION_ABI,
    functionName: 'vestingContract',
    chainId: larissaChain.id,
    query: {
      enabled: isConnected && isOnLarissaChain,
    }
  });

  // Get user's vesting info (limit)
  const { data: vestingInfo } = useReadContract({
    address: vestingContractAddress as Address,
    abi: VESTING_ABI,
    functionName: 'getVestingInfo',
    args: address ? [address] : undefined,
    chainId: larissaChain.id,
    query: {
      enabled: !!address && !!vestingContractAddress && isConnected && isOnLarissaChain,
    }
  });

  // Get user's total deposited
  const { data: userTotalDeposited, refetch: refetchUserTotalDeposited } = useReadContract({
    address: SILO_MIGRATION_CONTRACT,
    abi: SILO_MIGRATION_ABI,
    functionName: 'getUserTotalDeposited',
    args: address ? [address] : undefined,
    chainId: larissaChain.id,
    query: {
      enabled: !!address && isConnected && isOnLarissaChain,
    }
  });

  // Get token info
  const { data: tokenSymbolData } = useReadContract({
    address: migrationTokenAddress as Address,
    abi: ERC20_ABI,
    functionName: 'symbol',
    chainId: larissaChain.id,
    query: {
      enabled: !!migrationTokenAddress && isConnected && isOnLarissaChain,
    }
  });
  const tokenSymbol = tokenSymbolData as string | undefined;

  const { data: tokenDecimals } = useReadContract({
    address: migrationTokenAddress as Address,
    abi: ERC20_ABI,
    functionName: 'decimals',
    chainId: larissaChain.id,
    query: {
      enabled: !!migrationTokenAddress && isConnected && isOnLarissaChain,
    }
  });

  // Get user token balance
  const { data: tokenBalance, refetch: refetchTokenBalance } = useReadContract({
    address: migrationTokenAddress as Address,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: larissaChain.id,
    query: {
      enabled: !!address && !!migrationTokenAddress && isConnected && isOnLarissaChain,
    }
  });

  // Get token allowance
  const { data: tokenAllowance, refetch: refetchAllowance } = useReadContract({
    address: migrationTokenAddress as Address,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address ? [address, SILO_MIGRATION_CONTRACT] : undefined,
    chainId: larissaChain.id,
    query: {
      enabled: !!address && !!migrationTokenAddress && isConnected && isOnLarissaChain,
    }
  });

  // Get contract constants
  const { data: conversionRatio } = useReadContract({
    address: SILO_MIGRATION_CONTRACT,
    abi: SILO_MIGRATION_ABI,
    functionName: 'CONVERSION_RATIO',
    chainId: larissaChain.id,
    query: {
      enabled: isConnected && isOnLarissaChain,
    }
  });

  const { data: vestingMonths } = useReadContract({
    address: SILO_MIGRATION_CONTRACT,
    abi: SILO_MIGRATION_ABI,
    functionName: 'VESTING_MONTHS',
    chainId: larissaChain.id,
    query: {
      enabled: isConnected && isOnLarissaChain,
    }
  });

  // Get user deposit IDs
  const { data: depositIds, refetch: refetchDepositIds } = useReadContract({
    address: SILO_MIGRATION_CONTRACT,
    abi: SILO_MIGRATION_ABI,
    functionName: 'getUserDepositIds',
    args: address ? [address] : undefined,
    chainId: larissaChain.id,
    query: {
      enabled: !!address && isConnected && isOnLarissaChain,
    }
  });

  // Contract write hooks
  const { writeContract: approveToken, data: approveData } = useWriteContract();
  const { writeContract: depositTokens, data: depositData } = useWriteContract();
  const { writeContract: claimNative, data: claimData } = useWriteContract();
  const { writeContract: claimAllNative, data: claimAllData } = useWriteContract();

  // Wait for approval transaction
  const { isLoading: isApprovePending, isSuccess: isApproveSuccess } = useWaitForTransactionReceipt({
    hash: approveData,
  });

  // Wait for deposit transaction
  const { isLoading: isDepositPending, isSuccess: isDepositSuccess } = useWaitForTransactionReceipt({
    hash: depositData,
  });

  // Wait for claim transaction
  const { isLoading: isClaimPending, isSuccess: isClaimSuccess } = useWaitForTransactionReceipt({
    hash: claimData || claimAllData,
  });

  // Handle approval success
  useEffect(() => {
    if (isApproveSuccess) {
      setIsApproving(false);
      setTimeout(() => {
        refetchAllowance();
      }, 1000);
    }
  }, [isApproveSuccess, refetchAllowance]);

  // Handle deposit success
  useEffect(() => {
    if (isDepositSuccess) {
      setIsDepositing(false);
      setAmount("");
      setSuccessMessage("Tokens deposited successfully! You can now claim your first month's native coins.");
      setTimeout(() => {
        refetchTokenBalance();
        refetchAllowance();
        refetchDepositIds();
        refetchUserTotalDeposited();
        setSuccessMessage("");
      }, 5000);
    }
  }, [isDepositSuccess, refetchTokenBalance, refetchAllowance, refetchDepositIds, refetchUserTotalDeposited]);

  // Handle claim success
  useEffect(() => {
    if (isClaimSuccess) {
      setIsClaiming(false);
      setClaimingDepositId(null);
      setSuccessMessage("Native coins claimed successfully!");
      setTimeout(() => {
        refetchDepositIds();
        setSuccessMessage("");
      }, 5000);
    }
  }, [isClaimSuccess, refetchDepositIds]);

  // Helper functions
  const formatBalance = (balance: bigint | undefined, decimals: number = 18, fixValue: number = 6) => {
    if (!balance) return "0";
    return parseFloat(formatUnits(balance, decimals)).toFixed(fixValue);
  };

  const needsApproval = () => {
    if (!amount || !tokenDecimals) return false;
    try {
      const amountBigInt = parseUnits(amount, tokenDecimals as number);
      const allowance = tokenAllowance as bigint || BigInt(0);
      return allowance < amountBigInt;
    } catch {
      return false;
    }
  };

  const isValidAmount = () => {
    if (!amount || !tokenBalance || !tokenDecimals) return false;
    try {
      const depositLimit = getDepositLimit();
      if (depositLimit === null || depositLimit === BigInt(0)) return false;

      const amountBigInt = parseUnits(amount, tokenDecimals as number);
      const balance = tokenBalance as bigint;

      // Check balance
      if (amountBigInt > balance) return false;

      // Check user limit from vesting contract (wait for deposits read before allowing submit)
      if (vestingInfo) {
        if (userTotalDeposited === undefined) return false;
        const vestingLimit = BigInt((vestingInfo as any)['totalAmount']?.toString() || '0'); // totalAmount
        const totalDeposited = BigInt((userTotalDeposited as bigint)?.toString() || '0');
        const totalAfterDeposit = totalDeposited + amountBigInt;
        if (totalAfterDeposit > vestingLimit) return false;
      }

      return amountBigInt > BigInt(0);
    } catch {
      return false;
    }
  };

  const getRemainingLimit = () => {
    if (!vestingInfo || userTotalDeposited === undefined) return null;
    const vestingLimit = BigInt((vestingInfo as any)['totalAmount']?.toString() || '0'); // totalAmount
    const totalDeposited = BigInt((userTotalDeposited as bigint)?.toString() || '0');
    const remaining = vestingLimit - totalDeposited;
    return remaining > BigInt(0) ? remaining : BigInt(0);
  };

  const getDepositLimit = () => {
    if (!vestingInfo) return null;
    return BigInt((vestingInfo as any)["totalAmount"]?.toString() || "0");
  };

  // Handle approve
  const handleApprove = async () => {
    if (!migrationTokenAddress || !amount || !tokenDecimals) return;

    const depositLimit = getDepositLimit();
    if (depositLimit === null || depositLimit === BigInt(0)) {
      setError("No Deposit Limit found for this wallet. This migration is only available for wallets with an allocated deposit limit.");
      return;
    }

    setError("");
    setIsApproving(true);

    try {
      if (chain?.id !== larissaChain.id) {
        switchChain({ chainId: larissaChain.id });
        return;
      }

      const amountBigInt = parseUnits(amount, tokenDecimals as number);

      approveToken({
        address: migrationTokenAddress as Address,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [SILO_MIGRATION_CONTRACT, amountBigInt],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Approval failed");
      setIsApproving(false);
    }
  };

  // Handle deposit
  const handleDeposit = async () => {
    if (!migrationTokenAddress || !amount || !tokenDecimals) return;

    const depositLimit = getDepositLimit();
    if (depositLimit === null || depositLimit === BigInt(0)) {
      setError("No Deposit Limit found for this wallet. This migration is only available for wallets with an allocated deposit limit.");
      return;
    }

    if (needsApproval()) {
      setError("Please approve the token first before depositing");
      return;
    }

    setError("");
    setIsDepositing(true);

    try {
      if (chain?.id !== larissaChain.id) {
        switchChain({ chainId: larissaChain.id });
        return;
      }

      const amountBigInt = parseUnits(amount, tokenDecimals as number);

      depositTokens({
        address: SILO_MIGRATION_CONTRACT,
        abi: SILO_MIGRATION_ABI,
        functionName: 'depositTokens',
        args: [amountBigInt],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Deposit failed");
      setIsDepositing(false);
    }
  };

  // Handle claim for specific deposit
  const handleClaim = async (depositId: bigint) => {
    setError("");
    setIsClaiming(true);
    setClaimingDepositId(depositId);

    try {
      if (chain?.id !== larissaChain.id) {
        switchChain({ chainId: larissaChain.id });
        return;
      }

      claimNative({
        address: SILO_MIGRATION_CONTRACT,
        abi: SILO_MIGRATION_ABI,
        functionName: 'claimNative',
        args: [depositId],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Claim failed");
      setIsClaiming(false);
      setClaimingDepositId(null);
    }
  };

  // Handle claim all
  const handleClaimAll = async () => {
    setError("");
    setIsClaiming(true);

    try {
      if (chain?.id !== larissaChain.id) {
        switchChain({ chainId: larissaChain.id });
        return;
      }

      claimAllNative({
        address: SILO_MIGRATION_CONTRACT,
        abi: SILO_MIGRATION_ABI,
        functionName: 'claimAllNative',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Claim all failed");
      setIsClaiming(false);
    }
  };

  const remainingLimit = getRemainingLimit();
  const vestingLimit = vestingInfo ? BigInt((vestingInfo as any)['totalAmount']?.toString() || '0') : null;
  const hasDepositLimit = vestingLimit !== null && vestingLimit > BigInt(0);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/blockchain">Blockchain</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Silo Migration</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton />
            </header>

            <div className="py-6">
              <div className="flex items-center mb-6">
                <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                <h1 className="text-3xl font-bold">Silo Token Migration</h1>
              </div>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full"></div>

              {/* Wallet Connection Warning */}
              {!isConnected && (
                <Card className="bg-yellow-500/10 border border-yellow-500/20 mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <RiAlertLine className="size-6 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-lg font-semibold text-yellow-500 mb-2">Wallet Connection Required</h3>
                        <p className="text-sm text-yellow-500/80">
                          Please connect your wallet to migrate your ERC20 tokens to native coins. This feature only works on Larissa Chain.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Wrong Network Warning */}
              {isConnected && !isOnLarissaChain && (
                <Card className="bg-red-500/10 border border-red-500/20 mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <RiErrorWarningLine className="size-6 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-red-500 mb-2">Wrong Network</h3>
                        <p className="text-sm text-red-500/80 mb-4">
                          Token migration is only available on Larissa Chain. Please switch to Larissa Chain to continue.
                        </p>
                        <Button
                          onClick={() => switchChain({ chainId: larissaChain.id })}
                          className="bg-[#FF8A3C] text-white hover:bg-[#FF8A3C]/90"
                        >
                          Switch to Larissa Chain
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Error Message */}
              {error && (
                <Card className="bg-red-500/10 border border-red-500/20 mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <RiErrorWarningLine className="size-6 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-lg font-semibold text-red-500 mb-2">Transaction Failed</h3>
                        <p className="text-sm text-red-500/80">{error}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Success Message */}
              {successMessage && (
                <Card className="bg-green-500/10 border border-green-500/20 mb-6">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <RiCheckLine className="size-6 text-green-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-lg font-semibold text-green-500 mb-2">Success!</h3>
                        <p className="text-sm text-green-500/80">{successMessage}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {isConnected && isOnLarissaChain && (
                <div className="space-y-6">
                  {/* Eligibility / Deposit Limit Missing */}
                  {!hasDepositLimit && (
                    <Card className="bg-red-500/10 border border-red-500/20">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <RiErrorWarningLine className="size-6 text-red-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <h3 className="text-lg font-semibold text-red-500 mb-2">No Deposit Limit Found</h3>
                            <p className="text-sm text-red-500/80">
                              This wallet does not have an allocated <span className="font-medium">Deposit Limit</span> for Silo Migration, so deposits are disabled.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Deposit Section */}
                  <Card className="bg-sidebar border border-border">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <RiCoinsLine className="size-5 text-[#FF8A3C]" />
                        Migrate Tokens
                      </CardTitle>
                      <CardDescription>
                        Deposit your ERC20 tokens to receive native coins with vesting. First month is available immediately, remaining amount splits over {vestingMonths?.toString() || '6'} months.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Token Balance */}
                      {migrationTokenAddress && (
                        <div className="p-4 bg-[#FF8A3C]/10 border border-[#FF8A3C]/30 rounded-lg">
                          <div className="text-sm text-muted-foreground mb-1">Your {tokenSymbol || 'Token'} Balance</div>
                          <div className="text-2xl font-bold">
                            {formatBalance(tokenBalance as bigint, tokenDecimals as number || 18)} {tokenSymbol || 'TOKEN'}
                          </div>
                        </div>
                      )}

                      {/* User Limit Info */}
                      {hasDepositLimit && (
                        <div className="p-4 bg-background rounded-lg border border-border">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-sm text-muted-foreground mb-1">Your Deposit Limit</div>
                              <div className="text-lg font-semibold">
                                {formatBalance(vestingLimit, tokenDecimals as number || 18)} {tokenSymbol || 'TOKEN'}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-muted-foreground mb-1">Already Deposited</div>
                              <div className="text-lg font-semibold text-[#FF8A3C]">
                                {formatBalance(userTotalDeposited as bigint, tokenDecimals as number || 18)} {tokenSymbol || 'TOKEN'}
                              </div>
                            </div>
                          </div>
                          {remainingLimit !== null && (
                            <div className="mt-3 pt-3 border-t border-border">
                              <div className="text-sm text-muted-foreground mb-1">Presale Purchase</div>
                              <div className={`text-lg font-semibold ${remainingLimit === BigInt(0) ? 'text-red-500' : 'text-green-500'}`}>
                                {formatBalance(remainingLimit, tokenDecimals as number || 18)} {tokenSymbol || 'TOKEN'}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Conversion Info */}
                      {conversionRatio && (
                        <div className="p-4 bg-background rounded-lg border border-border">
                          <div className="text-sm text-muted-foreground mb-2">Conversion Rate</div>
                          <div className="text-lg font-semibold">
                            1 {tokenSymbol || 'Token'} = {conversionRatio.toString()} LRS
                          </div>
                        </div>
                      )}

                      {/* Amount Input */}
                      <div className="space-y-2">
                        <Label htmlFor="amount">Amount to Deposit</Label>
                        <div className="flex gap-2">
                          <Input
                            id="amount"
                            type="number"
                            placeholder="0.0"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="text-lg"
                            disabled={isDepositing || isApproving || !hasDepositLimit}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (
                                !tokenBalance ||
                                !tokenDecimals ||
                                remainingLimit === null ||
                                remainingLimit === BigInt(0)
                              ) {
                                return;
                              }
                              const balance = tokenBalance as bigint;
                              const maxAmount = balance < remainingLimit ? balance : remainingLimit;
                              setAmount(formatBalance(maxAmount, tokenDecimals as number));
                            }}
                            disabled={
                              !hasDepositLimit ||
                              !tokenBalance ||
                              remainingLimit === null ||
                              remainingLimit === BigInt(0)
                            }
                          >
                            MAX
                          </Button>
                        </div>
                        <div className="space-y-1">
                          {tokenBalance ? (
                            <p className="text-xs text-muted-foreground">
                              Available: {formatBalance(tokenBalance as bigint, tokenDecimals as number || 18)} {tokenSymbol || 'TOKEN'}
                            </p>
                          ) : null}
                          {remainingLimit !== null ? (
                            <p className="text-xs text-muted-foreground">
                              Remaining limit: {formatBalance(remainingLimit, tokenDecimals as number || 18)} {tokenSymbol || 'TOKEN'}
                            </p>
                          ) : null}
                        </div>
                      </div>

                      {/* Limit Warning */}
                      {remainingLimit !== null && remainingLimit === BigInt(0) && (
                        <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                          <RiAlertLine className="size-5 text-red-500 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-red-500">
                            <p className="font-medium">Deposit Limit Reached</p>
                            <p className="text-red-400 mt-1">
                              You have reached your maximum deposit limit. You cannot deposit more tokens.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Approval Warning */}
                      {needsApproval() && amount && (
                        <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                          <RiAlertLine className="size-5 text-blue-500 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-blue-500">
                            <p className="font-medium">Approval Required</p>
                            <p className="text-blue-400 mt-1">
                              You need to approve the migration contract to spend your {tokenSymbol || 'tokens'} before depositing.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="space-y-3">
                        {needsApproval() && (
                          <Button
                            className="w-full bg-blue-600 text-white hover:bg-blue-700"
                            onClick={handleApprove}
                            disabled={!hasDepositLimit || !isValidAmount() || isApproving || isApprovePending}
                          >
                            {isApproving || isApprovePending ? (
                              <>
                                <RiLoader4Line className="size-5 mr-2 animate-spin" />
                                Approving...
                              </>
                            ) : (
                              <>
                                <RiCheckLine className="size-5 mr-2" />
                                Approve {tokenSymbol || 'Token'}
                              </>
                            )}
                          </Button>
                        )}

                        <Button
                          className="w-full bg-[#FF8A3C] text-white hover:bg-[#FF8A3C]/90"
                          onClick={handleDeposit}
                          disabled={!hasDepositLimit || !isValidAmount() || needsApproval() || isDepositing || isDepositPending || (remainingLimit !== null && remainingLimit === BigInt(0))}
                        >
                          {isDepositing || isDepositPending ? (
                            <>
                              <RiLoader4Line className="size-5 mr-2 animate-spin" />
                              Depositing...
                            </>
                          ) : (
                            <>
                              <RiArrowDownLine className="size-5 mr-2" />
                              Deposit Tokens
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Deposits & Claims Section */}
                  <Card className="bg-sidebar border border-border">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <RiHistoryLine className="size-5 text-[#FF8A3C]" />
                            Your Deposits
                          </CardTitle>
                          <CardDescription>
                            View your deposits and claim vested native coins
                          </CardDescription>
                        </div>
                        {depositIds && Array.isArray(depositIds) && depositIds.length > 0 && (
                          <Button
                            variant="outline"
                            onClick={handleClaimAll}
                            disabled={isClaiming || isClaimPending}
                            className="border-[#FF8A3C] text-[#FF8A3C] hover:bg-[#FF8A3C]/10"
                          >
                            {isClaiming || isClaimPending ? (
                              <>
                                <RiLoader4Line className="size-4 mr-2 animate-spin" />
                                Claiming...
                              </>
                            ) : (
                              <>
                                <RiCheckLine className="size-4 mr-2" />
                                Claim All
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {!depositIds || !Array.isArray(depositIds) || depositIds.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                          <RiCoinsLine className="size-12 mx-auto mb-4 opacity-50" />
                          <p>No deposits yet. Deposit your tokens to get started.</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {depositIds.map((depositId, index) => {
                            return (
                              <DepositCard
                                key={index}
                                depositId={depositId}
                                address={address!}
                                onClaim={handleClaim}
                                isClaiming={isClaiming && claimingDepositId === depositId}
                                isClaimPending={isClaimPending}
                                chainId={larissaChain.id}
                              />
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

// Deposit Card Component
function DepositCard({
  depositId,
  address,
  onClaim,
  isClaiming,
  isClaimPending,
  chainId,
}: {
  depositId: bigint;
  address: Address;
  onClaim: (depositId: bigint) => void;
  isClaiming: boolean;
  isClaimPending: boolean;
  chainId: number;
}) {
  const { data: depositInfo } = useReadContract({
    address: SILO_MIGRATION_CONTRACT,
    abi: SILO_MIGRATION_ABI,
    functionName: 'getDepositInfo',
    args: [address, depositId],
    chainId: chainId,
    query: {
      enabled: !!address,
    }
  });

  const formatBalance = (balance: bigint | undefined, decimals: number = 18, fixValue: number = 6) => {
    if (!balance) return "0";
    return parseFloat(formatUnits(balance, decimals)).toFixed(fixValue);
  };

  const formatDate = (timestamp: bigint) => {
    if (!timestamp || timestamp === BigInt(0)) return 'N/A';
    return new Date(Number(timestamp) * 1000).toLocaleString();
  };

  const getTimeUntilNextClaim = (nextClaimTime: bigint) => {
    if (!nextClaimTime || nextClaimTime === BigInt(0)) return null;
    const now = Math.floor(Date.now() / 1000);
    const next = Number(nextClaimTime);
    if (next <= now) return "Available now";
    const diff = next - now;
    const days = Math.floor(diff / 86400);
    const hours = Math.floor((diff % 86400) / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (!depositInfo) {
    return (
      <Card className="bg-background border border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-center py-4">
            <RiLoader4Line className="size-5 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const [
    id,
    totalNativeAmount,
    claimedAmount,
    depositTime,
    tokensDeposited,
    claimableAmount,
    remainingAmount,
    nextClaimTime,
    exists,
  ] = depositInfo as [
    bigint,
    bigint,
    bigint,
    bigint,
    bigint,
    bigint,
    bigint,
    bigint,
    boolean,
  ];

  if (!exists) return null;

  const claimable = claimableAmount > BigInt(0);
  const fullyClaimed = remainingAmount === BigInt(0);
  const vestedPercentage = totalNativeAmount > BigInt(0)
    ? (Number(claimedAmount + claimableAmount) / Number(totalNativeAmount)) * 100
    : 0;

  return (
    <Card className="bg-background border border-border">
      <CardContent className="p-6">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-muted-foreground">Deposit ID</div>
              <div className="text-lg font-bold">#{id.toString()}</div>
            </div>
            <div className="text-right">
              <div className="text-sm text-muted-foreground">Deposited</div>
              <div className="text-lg font-bold">{formatDate(depositTime)}</div>
            </div>
          </div>

          {/* Amounts */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-border">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Tokens Deposited</div>
              <div className="font-semibold">{formatBalance(tokensDeposited, 18)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Total Native</div>
              <div className="font-semibold text-[#FF8A3C]">{formatBalance(totalNativeAmount)} LRS</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Claimed</div>
              <div className="font-semibold text-green-500">{formatBalance(claimedAmount)} LRS</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Remaining</div>
              <div className="font-semibold">{formatBalance(remainingAmount)} LRS</div>
            </div>
          </div>

          {/* Claimable Amount */}
          {claimable && (
            <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-green-500 font-medium mb-1">Claimable Now</div>
                  <div className="text-2xl font-bold text-green-500">
                    {formatBalance(claimableAmount)} LRS
                  </div>
                </div>
                <Button
                  onClick={() => onClaim(depositId)}
                  disabled={isClaiming || isClaimPending}
                  className="bg-green-500 text-white hover:bg-green-600"
                >
                  {isClaiming || isClaimPending ? (
                    <>
                      <RiLoader4Line className="size-4 mr-2 animate-spin" />
                      Claiming...
                    </>
                  ) : (
                    <>
                      <RiCheckLine className="size-4 mr-2" />
                      Claim
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Next Claim Time */}
          {!fullyClaimed && nextClaimTime > BigInt(0) && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <RiTimeLine className="size-4" />
              <span>
                {claimable
                  ? "Next claim available: "
                  : "Next claim in: "}
                {getTimeUntilNextClaim(nextClaimTime)}
              </span>
            </div>
          )}

          {/* Progress Bar */}
          {!fullyClaimed && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Vesting Progress</span>
                <span>{vestedPercentage.toFixed(1)}%</span>
              </div>
              <div className="w-full h-2 bg-background rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#FF8A3C] transition-all"
                  style={{ width: `${Math.min(vestedPercentage, 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Fully Claimed Badge */}
          {fullyClaimed && (
            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-center">
              <div className="flex items-center justify-center gap-2 text-green-500">
                <RiCheckLine className="size-5" />
                <span className="font-medium">Fully Claimed</span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
